// src/pages/ROHistory.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useROStore } from "../context/ROContext";

/* =======================
   Styles (inline for now)
======================= */
const pageWrapper = { padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1rem" };
const headerRow = { display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" };
const title = { fontSize: "1.25rem", fontWeight: 700, color: "#0f172a" };
const subtitle = { fontSize: "0.95rem", color: "#475569", marginTop: "0.25rem" };

const cardBase = {
  background: "#ffffff",
  borderRadius: "12px",
  padding: "1rem 1.25rem",
  boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
  border: "1px solid #e5e7eb",
};

const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "0.75rem" };
const field = { display: "flex", flexDirection: "column", gap: "0.35rem" };
const label = { fontSize: "0.8rem", color: "#64748b", fontWeight: 600 };

const input = {
  height: "36px",
  borderRadius: "10px",
  border: "1px solid #e5e7eb",
  padding: "0 0.75rem",
  outline: "none",
  fontSize: "0.95rem",
  background: "#fff",
};

const select = { ...input };

const tableWrap = { overflow: "auto", borderRadius: "12px", border: "1px solid #e5e7eb" };
const table = { width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: "1080px" };

const th = {
  textAlign: "left",
  fontSize: "0.8rem",
  color: "#475569",
  fontWeight: 700,
  background: "#f8fafc",
  padding: "0.75rem",
  borderBottom: "1px solid #e5e7eb",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const td = { padding: "0.75rem", borderBottom: "1px solid #f1f5f9", fontSize: "0.92rem", color: "#0f172a", verticalAlign: "top" };

const pillStyle = (bg, color) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "0.2rem 0.55rem",
  borderRadius: "999px",
  fontSize: "0.75rem",
  fontWeight: 700,
  background: bg,
  color,
  border: "1px solid rgba(15,23,42,0.08)",
  whiteSpace: "nowrap",
});

const btnRow = { display: "flex", gap: "0.5rem", flexWrap: "wrap" };
const btn = (variant = "primary") => ({
  height: "36px",
  borderRadius: "10px",
  border: variant === "ghost" ? "1px solid #e5e7eb" : "1px solid #0f172a",
  background: variant === "ghost" ? "#fff" : "#0f172a",
  color: variant === "ghost" ? "#0f172a" : "#fff",
  padding: "0 0.75rem",
  fontWeight: 700,
  cursor: "pointer",
});
const muted = { color: "#64748b" };

/* Drawer */
const overlay = { position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.45)", zIndex: 50 };
const drawer = {
  position: "fixed",
  top: 0,
  right: 0,
  height: "100vh",
  width: "min(520px, 92vw)",
  background: "#ffffff",
  borderLeft: "1px solid #e5e7eb",
  boxShadow: "-10px 0 30px rgba(15,23,42,0.18)",
  zIndex: 60,
  display: "flex",
  flexDirection: "column",
};
const drawerHeader = { padding: "1rem 1.25rem", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.75rem" };
const drawerBody = { padding: "1rem 1.25rem", overflow: "auto", display: "flex", flexDirection: "column", gap: "1rem" };
const drawerTitle = { fontSize: "1.05rem", fontWeight: 800, color: "#0f172a" };
const drawerSub = { fontSize: "0.85rem", color: "#64748b", marginTop: 4 };
const closeBtn = { height: 36, width: 36, borderRadius: 10, border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer", fontWeight: 900 };

const sectionTitle = { fontSize: "0.85rem", fontWeight: 900, color: "#0f172a", textTransform: "uppercase", letterSpacing: "0.08em" };
const kvGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" };
const kv = { border: "1px solid #e5e7eb", borderRadius: 12, padding: "0.75rem", background: "#fff" };
const kvK = { fontSize: "0.75rem", color: "#64748b", fontWeight: 800 };
const kvV = { marginTop: 6, fontSize: "0.92rem", color: "#0f172a", fontWeight: 600, whiteSpace: "pre-wrap" };

const timeline = { border: "1px solid #e5e7eb", borderRadius: 12, background: "#fff", overflow: "hidden" };
const timelineRow = { padding: "0.75rem", borderBottom: "1px solid #f1f5f9", display: "flex", flexDirection: "column", gap: 6 };
const timelineTop = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 };
const tType = { fontSize: "0.78rem", fontWeight: 900, color: "#0f172a" };
const tTime = { fontSize: "0.78rem", color: "#64748b", whiteSpace: "nowrap" };
const tMeta = { fontSize: "0.86rem", color: "#334155", whiteSpace: "pre-wrap" };

/* =======================
   Helpers
======================= */
function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function toISODateString(d) {
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  return `${yyyy}-${mm}-${dd}`;
}

function safeDate(isoLike) {
  if (!isoLike) return null;
  const d = new Date(isoLike);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtDateTime(isoLike) {
  const d = safeDate(isoLike);
  if (!d) return "—";
  return d.toLocaleString();
}

function includesCI(hay, needle) {
  return String(hay || "").toLowerCase().includes(String(needle || "").toLowerCase());
}

function downloadCSV(filename, rows, columns) {
  const escape = (v) => {
    const s = String(v ?? "");
    if (s.includes('"') || s.includes(",") || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const header = columns.map((c) => escape(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => escape(c.get(r))).join(",")).join("\n");

  const csv = `${header}\n${body}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function prettyMeta(meta) {
  if (!meta) return "";
  if (typeof meta === "string") return meta;
  try {
    if (typeof meta === "object") {
      const entries = Object.entries(meta)
        .filter(([, v]) => v !== undefined && v !== null && String(v) !== "")
        .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`);
      return entries.join("\n");
    }
    return String(meta);
  } catch {
    return String(meta);
  }
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(String(text || ""));
    return true;
  } catch {
    return false;
  }
}

/* =======================
   Component
======================= */
export default function ROHistory() {
  const store = useROStore();
  const navigate = useNavigate();

  // ✅ Wrap conditional arrays in useMemo (fixes react-hooks/exhaustive-deps warnings)
  const allROs = useMemo(() => {
    const a = Array.isArray(store?.repairOrders) ? store.repairOrders : null;
    const b = Array.isArray(store?.ros) ? store.ros : null;
    return a || b || [];
  }, [store?.repairOrders, store?.ros]);

  const technicians = useMemo(() => {
    const a = Array.isArray(store?.techs) ? store.techs : null;
    const b = Array.isArray(store?.technicians) ? store.technicians : null;
    return a || b || [];
  }, [store?.techs, store?.technicians]);

  const techNameById = useMemo(() => {
    const m = new Map();
    (technicians || []).forEach((t) => m.set(String(t.id), t.name));
    return m;
  }, [technicians]);

  const [selectedId, setSelectedId] = useState("");
  const selectedRO = useMemo(() => {
    if (!selectedId) return null;
    return (allROs || []).find((r) => String(r.id) === String(selectedId)) || null;
  }, [allROs, selectedId]);

  const [copied, setCopied] = useState("");

  const today = useMemo(() => new Date(), []);
  const defaultEnd = useMemo(() => toISODateString(today), [today]);
  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return toISODateString(d);
  }, []);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [status, setStatus] = useState("ALL");
  const [waiterOnly, setWaiterOnly] = useState("ALL");
  const [partsStatus, setPartsStatus] = useState("ALL");
  const [techId, setTechId] = useState("ALL");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const s = startDate ? new Date(`${startDate}T00:00:00`) : null;
    const e = endDate ? new Date(`${endDate}T23:59:59`) : null;

    return (allROs || [])
      .filter((ro) => {
        const pivotISO =
          ro.completedAtISO ||
          ro.timestamps?.completedAt ||
          ro.completedAt ||
          ro.timestamps?.createdAt ||
          ro.createdAt ||
          "";

        const pivot = safeDate(pivotISO);

        if (pivot && s && pivot < s) return false;
        if (pivot && e && pivot > e) return false;

        if (status !== "ALL") {
          const roStatus = String(ro.status || "").toUpperCase();
          const isCompleted =
            roStatus === "COMPLETED" ||
            roStatus.includes("COMPLETE") ||
            String(ro.lifecycle || "").toUpperCase() === "COMPLETED" ||
            !!ro.completedAtISO ||
            !!ro.timestamps?.completedAt;

          if (status === "COMPLETED" && !isCompleted) return false;
          if (status === "OPEN" && isCompleted) return false;
        }

        const isWaiter = String(ro.transportType || "").toUpperCase() === "WAITER" || ro.isWaiter === true;
        if (waiterOnly === "WAITER" && !isWaiter) return false;
        if (waiterOnly === "NOT_WAITER" && isWaiter) return false;

        if (partsStatus !== "ALL") {
          const ps = String(ro.partsStatus || "NONE").toUpperCase();
          if (ps !== partsStatus) return false;
        }

        if (techId !== "ALL") {
          const assigned = String(ro.assignedTechId || ro.techId || "");
          if (assigned !== String(techId)) return false;
        }

        if (search.trim()) {
          const q = search.trim();
          const hay = [
            ro.roNumber,
            ro.tagNumber, // ✅ include tag in search
            ro.id,
            ro.customerName,
            ro.vehicle,
            ro.concern,
            ro.notes,
            ro.advisorName,
            ro.assignedTechName,
          ].join(" | ");

          if (!includesCI(hay, q)) return false;
        }

        return true;
      })
      .sort((a, b) => {
        const ad =
          safeDate(a.completedAtISO || a.timestamps?.completedAt || a.timestamps?.createdAt || a.createdAt)?.getTime() || 0;
        const bd =
          safeDate(b.completedAtISO || b.timestamps?.completedAt || b.timestamps?.createdAt || b.createdAt)?.getTime() || 0;
        return bd - ad;
      });
  }, [allROs, startDate, endDate, status, waiterOnly, partsStatus, techId, search]);

  const summary = useMemo(() => {
    const total = filtered.length;
    const completed = filtered.filter((r) => {
      const st = String(r.status || "").toUpperCase();
      return (
        st === "COMPLETED" ||
        st.includes("COMPLETE") ||
        String(r.lifecycle || "").toUpperCase() === "COMPLETED" ||
        r.completedAtISO ||
        r.timestamps?.completedAt
      );
    }).length;

    const waiters = filtered.filter((r) => String(r.transportType || "").toUpperCase() === "WAITER" || r.isWaiter).length;
    return { total, completed, waiters };
  }, [filtered]);

  const columns = useMemo(
    () => [
      { label: "RO", get: (r) => r.roNumber ?? r.id ?? "—" },
      { label: "Tag", get: (r) => (safeString(r.tagNumber).trim() ? safeString(r.tagNumber).trim() : "—") }, // ✅
      { label: "Customer", get: (r) => r.customerName ?? "—" },
      { label: "Vehicle", get: (r) => r.vehicle ?? "—" },
      { label: "Advisor", get: (r) => r.advisorName ?? "—" },
      {
        label: "Tech",
        get: (r) => r.assignedTechName || techNameById.get(String(r.assignedTechId || r.techId || "")) || "—",
      },
      {
        label: "Waiter",
        get: (r) => (String(r.transportType || "").toUpperCase() === "WAITER" || r.isWaiter === true ? "YES" : "NO"),
      },
      { label: "Parts", get: (r) => String(r.partsStatus || "NONE").toUpperCase() },
      { label: "Status", get: (r) => String(r.status || "—") },
      { label: "Lifecycle", get: (r) => String(r.lifecycle || "—") },
      { label: "Promised", get: (r) => (r.promiseTimeISO ? fmtDateTime(r.promiseTimeISO) : "—") },
      {
        label: "Completed/Created",
        get: (r) => fmtDateTime(r.completedAtISO || r.timestamps?.completedAt || r.timestamps?.createdAt || r.createdAt),
      },
      { label: "Notes", get: (r) => r.notes ?? "—" },
    ],
    [techNameById]
  );

  function onReset() {
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setStatus("ALL");
    setWaiterOnly("ALL");
    setPartsStatus("ALL");
    setTechId("ALL");
    setSearch("");
  }

  function onExport() {
    const filename = `flowtrak_ro_history_${startDate || "start"}_to_${endDate || "end"}.csv`;
    downloadCSV(filename, filtered, columns);
  }

  const renderStatusPill = (ro) => {
    const st = String(ro.status || "").toUpperCase();
    const lc = String(ro.lifecycle || "").toUpperCase();
    const isCompleted =
      st === "COMPLETED" || st.includes("COMPLETE") || lc === "COMPLETED" || ro.completedAtISO || ro.timestamps?.completedAt;

    if (isCompleted) return <span style={pillStyle("#dcfce7", "#166534")}>COMPLETED</span>;
    if (st.includes("PART") || lc.includes("PART")) return <span style={pillStyle("#fff7ed", "#9a3412")}>WAITING PARTS</span>;
    if (st.includes("DISPATCH")) return <span style={pillStyle("#eff6ff", "#1d4ed8")}>DISPATCH</span>;
    if (st.includes("ASSIGN")) return <span style={pillStyle("#f5f3ff", "#5b21b6")}>ASSIGNED</span>;
    if (st.includes("PROGRESS")) return <span style={pillStyle("#e0f2fe", "#075985")}>IN PROGRESS</span>;
    return <span style={pillStyle("#f1f5f9", "#0f172a")}>{st || "OPEN"}</span>;
  };

  const roDetail = useMemo(() => {
    if (!selectedRO) return null;

    const isWaiter = String(selectedRO.transportType || "").toUpperCase() === "WAITER" || selectedRO.isWaiter === true;

    const tech =
      selectedRO.assignedTechName ||
      techNameById.get(String(selectedRO.assignedTechId || selectedRO.techId || "")) ||
      "—";

    const completed = selectedRO.completedAtISO || selectedRO.timestamps?.completedAt || selectedRO.completedAt || "";
    const created = selectedRO.timestamps?.createdAt || selectedRO.createdAt || "";
    const promised = selectedRO.promiseTimeISO || "";
    const concern = selectedRO.concern || "—";
    const notes = selectedRO.notes || "";
    const parts = String(selectedRO.partsStatus || "NONE").toUpperCase();
    const tag = safeString(selectedRO.tagNumber).trim();

    const history = Array.isArray(selectedRO.historyEvents) ? [...selectedRO.historyEvents] : [];
    history.sort((a, b) => {
      const at = safeDate(a?.ts)?.getTime() || 0;
      const bt = safeDate(b?.ts)?.getTime() || 0;
      return bt - at;
    });

    return { isWaiter, tech, completed, created, promised, parts, concern, notes, history, tag };
  }, [selectedRO, techNameById]);

  async function onCopy(text, key) {
    const ok = await copyToClipboard(text);
    if (!ok) return;
    setCopied(String(key || "copied"));
    window.setTimeout(() => setCopied(""), 1200);
  }

  function closeDrawer() {
    setSelectedId("");
    setCopied("");
  }

  function openInSearch() {
    if (!selectedRO) return;
    const roNum = selectedRO.roNumber || "";
    const qs = roNum ? `?q=${encodeURIComponent(roNum)}` : "";
    navigate(`/ro-search${qs}`, { state: { q: roNum, from: "ro-history" } });
  }

  useEffect(() => {
    if (!selectedRO) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeDrawer();
        return;
      }

      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const list = filtered || [];
        if (!list.length) return;

        const idx = list.findIndex((r) => String(r.id) === String(selectedId));
        const cur = idx >= 0 ? idx : 0;
        const nextIdx = e.key === "ArrowDown" ? Math.min(list.length - 1, cur + 1) : Math.max(0, cur - 1);
        const next = list[nextIdx];
        if (next?.id) setSelectedId(String(next.id));
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedRO, filtered, selectedId]);

  return (
    <div style={pageWrapper}>
      <div style={headerRow}>
        <div>
          <div style={title}>RO History</div>
          <div style={subtitle}>Review repair orders across a date range. Helpful for follow-ups, auditing, and performance trends.</div>
        </div>

        <div style={btnRow}>
          <button style={btn("ghost")} onClick={onReset}>Reset Filters</button>
          <button style={btn("primary")} onClick={onExport}>Export CSV</button>
        </div>
      </div>

      <div style={cardBase}>
        <div style={grid}>
          <div style={field}>
            <div style={label}>Start date</div>
            <input style={input} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>

          <div style={field}>
            <div style={label}>End date</div>
            <input style={input} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>

          <div style={field}>
            <div style={label}>Status</div>
            <select style={select} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="ALL">All</option>
              <option value="COMPLETED">Completed</option>
              <option value="OPEN">Open (not completed)</option>
            </select>
          </div>

          <div style={field}>
            <div style={label}>Waiter</div>
            <select style={select} value={waiterOnly} onChange={(e) => setWaiterOnly(e.target.value)}>
              <option value="ALL">All</option>
              <option value="WAITER">Waiter only</option>
              <option value="NOT_WAITER">Not waiter</option>
            </select>
          </div>

          <div style={field}>
            <div style={label}>Parts status</div>
            <select style={select} value={partsStatus} onChange={(e) => setPartsStatus(e.target.value)}>
              <option value="ALL">All</option>
              <option value="NONE">NONE</option>
              <option value="PULLED">PULLED</option>
              <option value="ORDERED">ORDERED</option>
              <option value="ARRIVED">ARRIVED</option>
              <option value="QUOTED">QUOTED</option>
            </select>
          </div>

          <div style={field}>
            <div style={label}>Technician</div>
            <select style={select} value={techId} onChange={(e) => setTechId(e.target.value)}>
              <option value="ALL">All techs</option>
              {(technicians || []).map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div style={{ ...field, gridColumn: "1 / -1" }}>
            <div style={label}>Search (RO, tag, customer, vehicle, notes)</div>
            <input style={input} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Type to search…" />
          </div>
        </div>

        <div style={{ marginTop: "0.75rem", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
          <div style={muted}><b>{summary.total}</b> results</div>
          <div style={muted}><b>{summary.completed}</b> completed in view</div>
          <div style={muted}><b>{summary.waiters}</b> waiters in view</div>
          {selectedRO ? (
            <div style={{ ...muted, marginLeft: "auto" }}>
              Tip: use <b>↑</b>/<b>↓</b> to move • <b>Esc</b> to close
            </div>
          ) : null}
        </div>
      </div>

      <div style={cardBase}>
        <div style={tableWrap}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>RO</th>
                <th style={th}>Tag</th>
                <th style={th}>Customer</th>
                <th style={th}>Vehicle</th>
                <th style={th}>Advisor</th>
                <th style={th}>Tech</th>
                <th style={th}>Waiter</th>
                <th style={th}>Parts</th>
                <th style={th}>Status</th>
                <th style={th}>Lifecycle</th>
                <th style={th}>Promised</th>
                <th style={th}>Completed/Created</th>
                <th style={th}>Notes</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((ro, idx) => {
                const roKey = ro.id || ro.roNumber || idx;

                const isWaiter = String(ro.transportType || "").toUpperCase() === "WAITER" || ro.isWaiter;
                const parts = String(ro.partsStatus || "NONE").toUpperCase();
                const techName = ro.assignedTechName || techNameById.get(String(ro.assignedTechId || ro.techId || "")) || "—";
                const active = String(selectedId) === String(ro.id);
                const tag = safeString(ro.tagNumber).trim();

                return (
                  <tr
                    key={roKey}
                    onClick={() => setSelectedId(String(ro.id))}
                    style={{ cursor: "pointer", background: active ? "#f8fafc" : "transparent" }}
                    title="Click to view details"
                  >
                    <td style={td}><b>{ro.roNumber ?? ro.id ?? "—"}</b></td>
                    <td style={td}>{tag ? `#${tag}` : "—"}</td>
                    <td style={td}>{ro.customerName ?? "—"}</td>
                    <td style={td}>{ro.vehicle ?? "—"}</td>
                    <td style={td}>{ro.advisorName ?? "—"}</td>
                    <td style={td}>{techName}</td>
                    <td style={td}>{isWaiter ? <span style={pillStyle("#e0f2fe", "#075985")}>WAITER</span> : "—"}</td>
                    <td style={td}><span style={pillStyle("#f1f5f9", "#0f172a")}>{parts}</span></td>
                    <td style={td}>{renderStatusPill(ro)}</td>
                    <td style={td}><span style={pillStyle("#f1f5f9", "#0f172a")}>{String(ro.lifecycle || "—").toUpperCase()}</span></td>
                    <td style={td}>{ro.promiseTimeISO ? fmtDateTime(ro.promiseTimeISO) : "—"}</td>
                    <td style={td}>{fmtDateTime(ro.completedAtISO || ro.timestamps?.completedAt || ro.timestamps?.createdAt || ro.createdAt)}</td>
                    <td style={td}>{ro.notes ?? "—"}</td>
                  </tr>
                );
              })}

              {filtered.length === 0 && (
                <tr>
                  <td style={{ ...td, color: "#64748b" }} colSpan={13}>
                    No repair orders match your filters. Try expanding the date range or clearing filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedRO && roDetail ? (
        <>
          <div style={overlay} onClick={closeDrawer} />
          <div style={drawer} role="dialog" aria-modal="true">
            <div style={drawerHeader}>
              <div>
                <div style={drawerTitle}>
                  RO {selectedRO.roNumber || selectedRO.id || "—"}{" "}
                  {roDetail.tag ? <span style={{ fontWeight: 900, opacity: 0.75 }}>• Tag #{roDetail.tag}</span> : null}
                </div>
                <div style={drawerSub}>{selectedRO.customerName || "—"} • {selectedRO.vehicle || "—"}</div>
              </div>

              <button style={closeBtn} onClick={closeDrawer} aria-label="Close">✕</button>
            </div>

            <div style={drawerBody}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {roDetail.isWaiter ? <span style={pillStyle("#e0f2fe", "#075985")}>WAITER</span> : null}
                <span style={pillStyle("#f1f5f9", "#0f172a")}>{String(selectedRO.partsStatus || "NONE").toUpperCase()}</span>
                {renderStatusPill(selectedRO)}
                <span style={pillStyle("#f1f5f9", "#0f172a")}>{String(selectedRO.lifecycle || "—").toUpperCase()}</span>
              </div>

              <div style={kvGrid}>
                <div style={kv}><div style={kvK}>Advisor</div><div style={kvV}>{selectedRO.advisorName || "—"}</div></div>
                <div style={kv}><div style={kvK}>Technician</div><div style={kvV}>{roDetail.tech}</div></div>
                <div style={kv}><div style={kvK}>Created</div><div style={kvV}>{fmtDateTime(roDetail.created)}</div></div>
                <div style={kv}><div style={kvK}>Promised</div><div style={kvV}>{roDetail.promised ? fmtDateTime(roDetail.promised) : "—"}</div></div>
                <div style={kv}><div style={kvK}>Completed</div><div style={kvV}>{roDetail.completed ? fmtDateTime(roDetail.completed) : "—"}</div></div>
                <div style={kv}><div style={kvK}>Flagged Hours</div><div style={kvV}>{Number.isFinite(Number(selectedRO.flaggedHours)) ? String(selectedRO.flaggedHours) : "—"}</div></div>
              </div>

              <div>
                <div style={sectionTitle}>Concern</div>
                <div style={{ ...kv, marginTop: 8 }}>
                  <div style={kvV}>{roDetail.concern || "—"}</div>
                  <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button style={btn("ghost")} onClick={() => onCopy(roDetail.concern || "", "concern")}>
                      {copied === "concern" ? "Copied ✓" : "Copy concern"}
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <div style={sectionTitle}>Notes</div>
                <div style={{ ...kv, marginTop: 8 }}>
                  <div style={kvV}>{roDetail.notes || "—"}</div>

                  <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button style={btn("ghost")} onClick={() => onCopy(roDetail.notes || "", "notes")}>
                      {copied === "notes" ? "Copied ✓" : "Copy notes"}
                    </button>
                    <button style={btn("primary")} onClick={openInSearch}>Open in RO Search</button>
                  </div>

                  <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
                    Tip: Esc closes • ↑/↓ moves between rows
                  </div>
                </div>
              </div>

              <div>
                <div style={sectionTitle}>Timeline</div>
                <div style={{ marginTop: 8, ...timeline }}>
                  {(roDetail.history || []).length === 0 ? (
                    <div style={{ padding: "0.9rem", color: "#64748b" }}>No history events recorded yet.</div>
                  ) : (
                    roDetail.history.map((evt, i) => (
                      <div key={`${evt.ts || i}`} style={{ ...timelineRow, borderBottom: i === roDetail.history.length - 1 ? "none" : timelineRow.borderBottom }}>
                        <div style={timelineTop}>
                          <div style={tType}>{String(evt.type || "EVENT")}</div>
                          <div style={tTime}>{fmtDateTime(evt.ts)}</div>
                        </div>
                        {evt.meta ? <div style={tMeta}>{prettyMeta(evt.meta)}</div> : <div style={{ ...tMeta, color: "#64748b" }}>—</div>}
                      </div>
                    ))
                  )}
                </div>

                <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button style={btn("ghost")} onClick={() => onCopy(JSON.stringify(selectedRO, null, 2), "json")}>
                    {copied === "json" ? "Copied ✓" : "Copy RO JSON"}
                  </button>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button style={btn("ghost")} onClick={closeDrawer}>Close</button>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
